import React, { useState, useEffect, createContext, useContext, useCallback } from 'react';
import { CheckCircle2, AlertCircle, Info, X } from 'lucide-react';

// Toast Types
type ToastType = 'success' | 'error' | 'info' | 'warning';

interface Toast {
    id: string;
    message: string;
    type: ToastType;
    duration?: number;
}

interface ToastContextType {
    showToast: (message: string, type?: ToastType, duration?: number) => void;
}

const ToastContext = createContext<ToastContextType | null>(null);

// Hook to use toast
export const useToast = () => {
    const context = useContext(ToastContext);
    if (!context) {
        throw new Error('useToast must be used within ToastProvider');
    }
    return context;
};

// Individual Toast Component
const ToastItem: React.FC<{ toast: Toast; onRemove: (id: string) => void }> = ({ toast, onRemove }) => {
    useEffect(() => {
        const timer = setTimeout(() => onRemove(toast.id), toast.duration || 4000);
        return () => clearTimeout(timer);
    }, [toast.id, toast.duration, onRemove]);

    const icons = {
        success: <CheckCircle2 size={20} />,
        error: <AlertCircle size={20} />,
        info: <Info size={20} />,
        warning: <AlertCircle size={20} />
    };

    const colors = {
        success: 'from-green-500 to-emerald-600 border-green-400/50',
        error: 'from-red-500 to-rose-600 border-red-400/50',
        info: 'from-blue-500 to-indigo-600 border-blue-400/50',
        warning: 'from-amber-500 to-orange-600 border-amber-400/50'
    };

    const bgColors = {
        success: 'bg-green-500/10',
        error: 'bg-red-500/10',
        info: 'bg-blue-500/10',
        warning: 'bg-amber-500/10'
    };

    return (
        <div
            className={`
        flex items-center gap-3 px-4 py-3 rounded-xl
        bg-white/90 backdrop-blur-xl border shadow-2xl
        animate-[slideIn_0.3s_ease-out] 
        hover:scale-[1.02] transition-transform cursor-pointer
        ${colors[toast.type]}
      `}
            onClick={() => onRemove(toast.id)}
        >
            {/* Icon */}
            <div className={`p-2 rounded-lg ${bgColors[toast.type]} bg-gradient-to-br ${colors[toast.type].split(' ')[0]} ${colors[toast.type].split(' ')[1]} text-white`}>
                {icons[toast.type]}
            </div>

            {/* Message */}
            <p className="flex-1 text-sm font-medium text-gray-800 pr-2">
                {toast.message}
            </p>

            {/* Close button */}
            <button
                onClick={(e) => { e.stopPropagation(); onRemove(toast.id); }}
                className="p-1 hover:bg-gray-100 rounded-full transition-colors text-gray-400 hover:text-gray-600"
            >
                <X size={14} />
            </button>
        </div>
    );
};

// Toast Provider Component
export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [toasts, setToasts] = useState<Toast[]>([]);

    const showToast = useCallback((message: string, type: ToastType = 'info', duration = 4000) => {
        const id = Date.now().toString() + Math.random().toString(36).substr(2, 9);
        setToasts(prev => [...prev, { id, message, type, duration }]);
    }, []);

    const removeToast = useCallback((id: string) => {
        setToasts(prev => prev.filter(t => t.id !== id));
    }, []);

    return (
        <ToastContext.Provider value={{ showToast }}>
            {children}

            {/* Toast Container - Bottom Right */}
            <div className="fixed bottom-6 right-6 z-[200] flex flex-col gap-3 max-w-sm w-full pointer-events-none">
                {toasts.map(toast => (
                    <div key={toast.id} className="pointer-events-auto">
                        <ToastItem toast={toast} onRemove={removeToast} />
                    </div>
                ))}
            </div>
        </ToastContext.Provider>
    );
};

// CSS Animation (add to index.css if needed)
// @keyframes slideIn {
//   from { transform: translateX(100%); opacity: 0; }
//   to { transform: translateX(0); opacity: 1; }
// }
